import { useState } from 'react'
import { LoadingAnimation, Flex } from '@/components'
import { Plan } from '@/types/plan'
import clsx from 'clsx'
import Select, { SelectOption } from '@/app/components/Select'

/** Sentinel option id for the "+ Add my plan" row (real plan ids are positive). */
export const ADD_PLAN_OPTION_ID = -1

export interface Props {
  planOptions: Plan[]
  isLoading: boolean
  label?: string
  selectedPlanId?: number
  onChange?: (planId: number) => void
  className?: string
  required?: boolean
  disabled?: boolean
  /** When set, an "+ Add my plan" row is shown that opens the manual-plan flow. */
  onAddPlan?: () => void
}

export default function UtilityPlanDropdown({
  planOptions,
  isLoading,
  label = 'Select a plan',
  selectedPlanId,
  onChange,
  className,
  required = false,
  disabled = false,
  onAddPlan,
}: Props) {
  const [internalId, setInternalId] = useState<number | null>(
    selectedPlanId ?? null,
  )

  // keep controlled + uncontrolled in sync
  const currentId = selectedPlanId ?? internalId
  const handleSelect = (id: number) => {
    if (id === ADD_PLAN_OPTION_ID) {
      onAddPlan?.()
      return
    }
    setInternalId(id)
    onChange?.(id)
  }

  const title = (p: Plan) => (p.is_default ? `[Most Common] ${p.name}` : p.name)

  if (isLoading) {
    return (
      <Flex container justifyContent="center">
        <LoadingAnimation type="flashing" />
      </Flex>
    )
  }

  const plans: SelectOption<number>[] = planOptions.map((plan) => ({
    id: plan.id,
    name: title(plan),
  }))

  if (onAddPlan) {
    plans.push({
      id: ADD_PLAN_OPTION_ID,
      name: '+ Add my own plan',
      action: true,
    })
  }

  return (
    <div className={clsx('h-fit', className)}>
      <Select
        options={plans}
        onChange={handleSelect}
        required={required}
        label={label}
        selectedOptionId={currentId ?? undefined}
        disabled={disabled}
      />
    </div>
  )
}
